import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Chip, Grid} from 'views/components';
import {Translate, translate} from 'views/module';
import {Favorite} from 'views/icons';

const Careplan = (props) => {
  const {newJob, updateJobCarePlan, setIsStepValid} = props;

  const carePlan = newJob.carePlan || {};
  const selectedCareNeeds = carePlan.careNeeds || [];

  // Care options array - get keys from translation object
  const careOptionsObject = translate('career.create.CarePlan.Options');
  const careOptions = Object.keys(careOptionsObject);

  // Update step validity when care needs change
  React.useEffect(() => {
    const isValid = selectedCareNeeds.length > 0;
    setIsStepValid(isValid);
  }, [selectedCareNeeds, setIsStepValid]);

  const handleCareNeedToggle = (careNeed) => {
    updateJobCarePlan((carePlan) => {
      const currentNeeds = carePlan.careNeeds || [];
      const isSelected = currentNeeds.includes(careNeed);

      const updatedNeeds = isSelected ?
        currentNeeds.filter((need) => need !== careNeed) :
        [...currentNeeds, careNeed];

      return {
        ...carePlan,
        careNeeds: updatedNeeds,
      };
    });
  };

  const getSelectedDescriptions = () => {
    return selectedCareNeeds.map((need) => ({
      key: need,
      label: translate(`career.create.CarePlan.Options.${need}.Label`),
      description: translate(`career.create.CarePlan.Options.${need}.Description`),
    }));
  };

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.CarePlan.Title" />
      </Typography>

      {/* Care Needs Selection */}
      <Box sx={{mb: 4, textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
          <Translate expression="career.create.CarePlan.CareNeedsLabel" />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          <Translate expression="career.create.CarePlan.CareNeedsDescription" />
        </Typography>

        {/* Care Options Grid */}
        <Grid container spacing={1.5} justifyContent="center">
          {
            careOptions.map((option) => {
              const isSelected = selectedCareNeeds.includes(option);
              const label = translate(`career.create.CarePlan.Options.${option}.Label`);

              return (
                <Grid item key={option}>
                  <Chip
                    label={label}
                    onClick={() => handleCareNeedToggle(option)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </Box>

      {/* Selected Care Needs Summary */}
      {
        selectedCareNeeds.length > 0 &&
        <Box sx={{textAlign: 'center'}}>
          <Typography variant="subtitle1" sx={{mb: 2, fontWeight: 500}}>
            <Translate expression="career.create.CarePlan.SelectedCareNeedsTitle" />
          </Typography>

          <Box sx={{maxWidth: 900, mx: 'auto', textAlign: 'left'}}>
            {/* Individual descriptions */}
            <Box sx={{mb: 3}}>
              {
                getSelectedDescriptions().map((item) => (
                  <Typography key={item.key} variant="body2" sx={{mb: 1}}>
                    <Typography component="span" sx={{fontWeight: 600}}>{item.label}:</Typography> {item.description}
                  </Typography>
                ))
              }
            </Box>

            {/* Summary box */}
            <Box
              sx={{
                p: 3,
                backgroundColor: '#fdf2f8',
                borderRadius: 2,
                border: '1px solid #fce7f3',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Favorite sx={{color: '#ec4899', mt: 0.5}} />
              <Box>
                <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#ec4899', mb: 0.5}}>
                  <Translate
                    expression="career.create.CarePlan.SelectedCareNeedsCount"
                    substitute={{count: selectedCareNeeds.length}}
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getSelectedDescriptions().map((item) => item.label).join(', ')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      }
    </Box>
  );
};

Careplan.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobCarePlan: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default Careplan;
